import React from 'react';

import type { TeamMember } from '../types';

interface TeamGridProps {
  team: TeamMember[];
  loading?: boolean;
  error?: string | null;
  emptyLabel: string;
}

const SkeletonCard = () => (
  <div className="animate-pulse rounded-xl border-2 border-slate-200 bg-white p-4 text-left">
    <div className="mb-3 flex items-center gap-3">
      <div className="h-12 w-12 rounded-full bg-slate-200" />
      <div className="flex-1">
        <div className="mb-2 h-3 w-2/3 rounded bg-slate-200" />
        <div className="h-3 w-1/3 rounded bg-slate-200" />
      </div>
    </div>
    <div className="mb-3 grid grid-cols-2 gap-2">
      <div className="rounded-lg bg-slate-50 p-4" />
      <div className="rounded-lg bg-slate-50 p-4" />
    </div>
  </div>
);

const renderEmptyState = (label: string) => (
  <div className="rounded-xl border-2 border-slate-200 bg-white p-8 text-center text-slate-600">
    <div className="mb-2 text-3xl"><span role="img" aria-label="no data">🗂️</span></div>
    <div className="font-semibold">Aucun membre trouvé pour {label}</div>
    <div className="text-sm">Les collaborateurs s’afficheront ici dès qu’ils seront attribués à la copro.</div>
  </div>
);

const MemberCard = ({ m }: { m: TeamMember }) => (
  <div className="rounded-xl border-2 border-slate-200 bg-white p-4 text-left">
    <div className="mb-3 flex items-center gap-3">
      <div className="flex h-12 w-12 items-center justify-center rounded-full font-bold text-white" style={{ background: m.type === 'vente' ? 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)' : 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' }}>{m.initiales}</div>
      <div>
        <div className="font-bold">{m.nom}</div>
        <div className="text-sm text-slate-600">{m.role}</div>
      </div>
    </div>
    <div className="mb-3 grid grid-cols-3 gap-2">
      <div className="rounded-lg bg-slate-50 p-2 text-center">
        <div className="font-bold text-rgpd-blue">{m.rdvSemaineEnCours}</div>
        <div className="text-xs text-slate-600">RDV semaine en cours</div>
      </div>
      <div className="rounded-lg bg-slate-50 p-2 text-center">
        <div className="font-bold text-rgpd-blue">{m.rdvSemaineAVenir}</div>
        <div className="text-xs text-slate-600">RDV semaine à venir</div>
      </div>
      <div className="rounded-lg bg-slate-50 p-2 text-center">
        <div className="font-bold text-rgpd-blue">{m.tauxReussite}%</div>
        <div className="text-xs text-slate-600">Taux réussite</div>
      </div>
    </div>
  </div>
);

function TeamGrid({ team, loading, error, emptyLabel }: TeamGridProps) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={`skel-${i}`} />)}
      {!loading && error && (
        <div className="col-span-full">{renderEmptyState(emptyLabel)}</div>
      )}
      {!loading && !error && team.length === 0 && (
        <div className="col-span-full">{renderEmptyState(emptyLabel)}</div>
      )}
      {!loading && !error && team.map((m) => <MemberCard key={m.id} m={m} />)}
    </div>
  );
}

export default TeamGrid;


